import React, { useState, useContext } from 'react';
import { Typography, Input, Button, Table, Tag, message, Grid } from 'antd';
import Axios from 'axios';
import { apiUrl } from '../utils/config';
import { Context } from '../utils/store';
import { handleTokenExpired } from '../utils/utils';

const { Title } = Typography;
const { Search } = Input;
const { useBreakpoint } = Grid;

interface IUserRow {
  firstname: string;
  lastname: string;
  email: string;
  dormitory: string;
  groups: string;
  status: number;
}

const statusMap: Record<number, { color: string; text: string }> = {
  1: { color: 'red', text: 'Not Activated' },
  2: { color: 'gold', text: 'Waiting' },
  3: { color: 'green', text: 'Activated' },
  0: { color: 'default', text: 'Deleted' },
};

const UserSearch: React.FC = () => {
  const { state, dispatch } = useContext(Context);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<IUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  const handleSearch = async (val?: string) => {
    const q = (val ?? query).trim();
    if (!q) {
      message.error('Enter a search query');
      return;
    }
    setLoading(true);
    try {
      const { data } = await Axios.get(
        `${apiUrl}/user/search/?query=${encodeURIComponent(q)}`,
        { headers: { Authorization: `JWT ${state.token}` } },
      );
      setResults(data);
    } catch (error) {
      const err = error as any;
      if (
        !handleTokenExpired(err.response?.status, () =>
          dispatch({ type: 'Logout', payload: null }),
        )
      ) {
        message.error('Error fetching users');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'First Name', dataIndex: 'firstname', key: 'firstname', width: 130 },
    { title: 'Last Name', dataIndex: 'lastname', key: 'lastname', width: 130 },
    { title: 'Email', dataIndex: 'email', key: 'email', width: 230 },
    { title: 'Dormitory', dataIndex: 'dormitory', key: 'dormitory', width: 110 },
    { title: 'Groups', dataIndex: 'groups', key: 'groups', width: 140 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (s: number) => {
        const { color, text } = statusMap[s] || { color: 'blue', text: 'Unknown' };
        return <Tag color={color}>{text}</Tag>;
      },
    },
  ];

  return (
    <div style={{ padding: 16, boxSizing: 'border-box', width: '100%' }}>
      <Title level={3}>User Search</Title>

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          <Input
            placeholder="Enter first name or last name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            allowClear
            style={{ width: '100%' }}
          />
          <Button
            type="primary"
            loading={loading}
            onClick={() => handleSearch()}
            style={{ width: '100%' }}
          >
            Search
          </Button>
        </div>
      ) : (
        <Search
          placeholder="Enter first name or last name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onSearch={handleSearch}
          allowClear
          enterButton="Search"
          size="large"
          style={{ width: '100%', maxWidth: 600 }}
        />
      )}

      <div style={{ marginTop: 16, overflowX: 'auto', width: '100%' }}>
        <Table
          columns={columns}
          dataSource={results}
          rowKey={(r) => r.email}
          pagination={{ position: ['bottomRight'] }}
          scroll={{ x: 'max-content' }}
          size="small"
          loading={loading}
        />
      </div>
    </div>
  );
};

export default UserSearch;

